import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Settings as SettingsIcon,
    Lock,
    Eye,
    EyeOff,
    Bell,
    Shield,
    AlertTriangle,
    Loader2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/store/useAuthStore';

const Settings = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user, logout, setUser } = useAuthStore();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');

    // Notification preferences - initialise from user data
    const [emailNotifications, setEmailNotifications] = useState(true);
    const [courseReminders, setCourseReminders] = useState(true);
    const [wellnessUpdates, setWellnessUpdates] = useState(true);

    // Load notification preferences from user data
    useEffect(() => {
        if (user?.client) {
            // emailNotifications from backend controls all email preferences
            const emailEnabled = user.client.emailNotifications ?? true;
            setEmailNotifications(emailEnabled);
            setCourseReminders(emailEnabled);
            setWellnessUpdates(emailEnabled);
        }
    }, [user]);

    // Change password mutation (would need to add endpoint to backend)
    const changePasswordMutation = useMutation({
        mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
            const response = await api.post('/auth/change-password', data);
            return response.data;
        },
        onSuccess: () => {
            toast.success('Password changed successfully!');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        },
        onError: (error: any) => {
            const message = error.response?.data?.message || 'Failed to change password';
            toast.error(message);
        },
    });

    const handleChangePassword = (e: React.FormEvent) => {
        e.preventDefault();

        if (newPassword.length < 8) {
            toast.error('New password must be at least 8 characters long');
            return;
        }

        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        changePasswordMutation.mutate({
            currentPassword,
            newPassword,
        });
    };

    // Update notification preferences mutation
    const updateNotificationsMutation = useMutation({
        mutationFn: async (data: { emailNotifications: boolean; smsNotifications: boolean }) => {
            const response = await api.put('/users/me', {
                client: data,
            });
            return response.data;
        },
        onSuccess: (updatedUser) => {
            toast.success('Notification preferences saved');
            // Update the user in the auth store with new data
            if (updatedUser) {
                setUser(updatedUser);
            }
            queryClient.invalidateQueries({ queryKey: ['currentUser'] });
        },
        onError: (error: any) => {
            const message =
                error.response?.data?.message || 'Failed to save notification preferences';
            toast.error(message);
        },
    });

    // Handle notification toggle changes - save immediately
    const handleEmailNotificationsChange = (enabled: boolean) => {
        setEmailNotifications(enabled);
        // When master email toggle is off, disable all email-based notifications
        if (!enabled) {
            setCourseReminders(false);
            setWellnessUpdates(false);
        }
        updateNotificationsMutation.mutate({
            emailNotifications: enabled,
            smsNotifications: false, // SMS not implemented yet
        });
    };

    // Delete account mutation
    const deleteAccountMutation = useMutation({
        mutationFn: async () => {
            if (!user?.id) throw new Error('User not found');
            await api.delete(`/users/${user.id}`);
        },
        onSuccess: () => {
            toast.success('Account deleted successfully');
            logout();
            navigate('/login');
        },
        onError: (error: any) => {
            const message = error.response?.data?.message || 'Failed to delete account';
            toast.error(message);
        },
    });

    const handleDeleteAccount = () => {
        if (deleteConfirmText !== 'DELETE') {
            toast.error('Please type DELETE to confirm');
            return;
        }
        deleteAccountMutation.mutate();
    };

    return (
        <div className="max-w-4xl mx-auto px-6 py-8">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center">
                    <SettingsIcon className="w-10 h-10 mr-3 text-emerald-600" />
                    Settings
                </h1>
                <p className="text-gray-600 text-lg">
                    Manage your account settings and preferences
                </p>
            </div>

            <div className="space-y-6">
                {/* Security Settings */}
                <Card className="p-6">
                    <div className="flex items-center space-x-3 mb-6">
                        <Shield className="w-6 h-6 text-emerald-600" />
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Security</h2>
                            <p className="text-sm text-gray-600">
                                Manage your password and security settings
                            </p>
                        </div>
                    </div>

                    <form onSubmit={handleChangePassword} className="space-y-4">
                        <div>
                            <Label htmlFor="currentPassword">Current Password</Label>
                            <div className="relative mt-1">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <Input
                                    id="currentPassword"
                                    type={showCurrentPassword ? 'text' : 'password'}
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    className="pl-10 pr-10"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showCurrentPassword ? (
                                        <EyeOff className="w-5 h-5" />
                                    ) : (
                                        <Eye className="w-5 h-5" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="newPassword">New Password</Label>
                            <div className="relative mt-1">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <Input
                                    id="newPassword"
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="pl-10 pr-10"
                                    minLength={8}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showNewPassword ? (
                                        <EyeOff className="w-5 h-5" />
                                    ) : (
                                        <Eye className="w-5 h-5" />
                                    )}
                                </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
                        </div>

                        <div>
                            <Label htmlFor="confirmPassword">Confirm New Password</Label>
                            <div className="relative mt-1">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <Input
                                    id="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="pl-10 pr-10"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showConfirmPassword ? (
                                        <EyeOff className="w-5 h-5" />
                                    ) : (
                                        <Eye className="w-5 h-5" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={changePasswordMutation.isPending}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                            {changePasswordMutation.isPending
                                ? 'Changing Password...'
                                : 'Change Password'}
                        </Button>
                    </form>
                </Card>

                {/* Notification Settings */}
                <Card className="p-6">
                    <div className="flex items-center space-x-3 mb-6">
                        <Bell className="w-6 h-6 text-emerald-600" />
                        <div className="flex-1">
                            <h2 className="text-xl font-bold text-gray-900">Notifications</h2>
                            <p className="text-sm text-gray-600">
                                Manage how you receive notifications
                            </p>
                        </div>
                        {updateNotificationsMutation.isPending && (
                            <Loader2 className="w-5 h-5 text-emerald-600 animate-spin" />
                        )}
                    </div>

                    <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div>
                                <p className="font-medium text-gray-900">Email Notifications</p>
                                <p className="text-sm text-gray-600">Receive updates via email</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={emailNotifications}
                                    onChange={(e) =>
                                        handleEmailNotificationsChange(e.target.checked)
                                    }
                                    disabled={updateNotificationsMutation.isPending}
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
                            </label>
                        </div>

                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div>
                                <p
                                    className={`font-medium ${emailNotifications ? 'text-gray-900' : 'text-gray-400'}`}
                                >
                                    Course Reminders
                                </p>
                                <p
                                    className={`text-sm ${emailNotifications ? 'text-gray-600' : 'text-gray-400'}`}
                                >
                                    Get reminders about your courses
                                </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={courseReminders && emailNotifications}
                                    onChange={(e) => setCourseReminders(e.target.checked)}
                                    disabled={!emailNotifications}
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
                            </label>
                        </div>

                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div>
                                <p
                                    className={`font-medium ${emailNotifications ? 'text-gray-900' : 'text-gray-400'}`}
                                >
                                    Wellness Updates
                                </p>
                                <p
                                    className={`text-sm ${emailNotifications ? 'text-gray-600' : 'text-gray-400'}`}
                                >
                                    Receive wellness tips and progress updates
                                </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={wellnessUpdates && emailNotifications}
                                    onChange={(e) => setWellnessUpdates(e.target.checked)}
                                    disabled={!emailNotifications}
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
                            </label>
                        </div>

                        <p className="text-sm text-gray-500 mt-4">
                            Note: Email delivery is being configured. Your preferences will take
                            effect once active.
                        </p>
                    </div>
                </Card>

                {/* Danger Zone */}
                <Card className="p-6 border-red-200">
                    <div className="mb-4">
                        <h2 className="text-xl font-bold text-red-600">Danger Zone</h2>
                        <p className="text-sm text-gray-600">Irreversible actions</p>
                    </div>

                    <div className="space-y-3">
                        <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                            <p className="font-medium text-gray-900 mb-2">Delete Account</p>
                            <p className="text-sm text-gray-600 mb-3">
                                Permanently delete your account and all associated data. This action
                                cannot be undone.
                            </p>
                            <Button
                                variant="outline"
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => setShowDeleteConfirm(true)}
                            >
                                Delete My Account
                            </Button>
                        </div>
                    </div>
                </Card>

                {/* About */}
                <Card className="p-6">
                    <div className="mb-4">
                        <h2 className="text-xl font-bold text-foreground">About</h2>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Version</span>
                        <span className="font-mono text-sm text-foreground">
                            v{__APP_VERSION__}
                        </span>
                    </div>
                </Card>
            </div>

            {/* Delete Account Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <div className="flex items-center space-x-3 mb-4">
                            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                                <AlertTriangle className="w-6 h-6 text-red-600" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">Delete Account</h2>
                                <p className="text-sm text-gray-600">
                                    This action cannot be undone
                                </p>
                            </div>
                        </div>

                        <div className="mb-6">
                            <p className="text-gray-700 mb-4">
                                Are you absolutely sure you want to delete your account? All of your
                                data will be permanently removed, including:
                            </p>
                            <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                                <li>Your profile and personal information</li>
                                <li>All journal entries and meal logs</li>
                                <li>Course progress and enrollments</li>
                                <li>Shopping lists and pantry data</li>
                            </ul>
                        </div>

                        <div className="mb-6">
                            <Label htmlFor="deleteConfirm">
                                Type <span className="font-bold">DELETE</span> to confirm
                            </Label>
                            <Input
                                id="deleteConfirm"
                                type="text"
                                value={deleteConfirmText}
                                onChange={(e) => setDeleteConfirmText(e.target.value)}
                                placeholder="DELETE"
                                className="mt-2"
                            />
                        </div>

                        <div className="flex space-x-3">
                            <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => {
                                    setShowDeleteConfirm(false);
                                    setDeleteConfirmText('');
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="outline"
                                className="flex-1 text-red-600 border-red-300 hover:bg-red-50"
                                onClick={handleDeleteAccount}
                                disabled={
                                    deleteAccountMutation.isPending ||
                                    deleteConfirmText !== 'DELETE'
                                }
                            >
                                {deleteAccountMutation.isPending ? 'Deleting...' : 'Delete Account'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Settings;
